import { Injectable } from '@angular/core';
import { of, map, tap, Observable } from 'rxjs';
import { ContactType } from 'src/app/core/models/common/contact.model';
import { FiscalIdKind } from 'src/app/core/models/common/fiscal-id.model';
import { Municipality } from 'src/app/core/models/common/municipality.model';
import { Province } from 'src/app/core/models/common/province.model';
import { Lead } from 'src/app/core/models/lead/lead.model';
import { LeadCreateDto } from 'src/app/infrastructure/lobby-proxy/DTOs/lead-create.dto';
import { BusinessSectorProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-business-sector/business-sector-proxy.service';
import { LeadProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-lead/lead-proxy.service';
import { ItalianRegistryProxyService } from 'src/app/infrastructure/lobby-proxy/lobby-proxy-registry/italian-registry-proxy.service';
import { businessSectorDtoToBusinessSector } from 'src/app/shared/mappings/common-model-dto/business-sector-dto.mapping';
import { comuneDtoToMunicipality } from 'src/app/shared/mappings/common-model-dto/municipality-dto.mapping';
import { provinceDtoToProvince } from 'src/app/shared/mappings/common-model-dto/province-dto.mapping';
import { LeadDryrunError, dryrunErrors } from 'src/app/shared/utils/pipes/dryrun-lead-error.pipe';
import { throwIfLobbyErrorCode } from 'src/app/shared/utils/pipes/throwIfLobbyErrorCode.pipe';

@Injectable({
  providedIn: 'root'
})
export class LeadCreateFormService {

  private municipalityCache = new Map<number, Municipality>()
  private provinceCache = new Map<number, Province>()

  constructor(private readonly leadProxyService: LeadProxyService,
    private readonly italianRegistryProxyService: ItalianRegistryProxyService,
    private readonly businessSectorProxyService: BusinessSectorProxyService) { }

  getMunicipality$(municipalityId: number) {

    const cachedValue = this.municipalityCache.get(municipalityId)
    if (cachedValue) return of(cachedValue)

    return this.italianRegistryProxyService.getMunicipalityById$(municipalityId).pipe(
      throwIfLobbyErrorCode,
      map(dto => comuneDtoToMunicipality(dto.data)!),
      tap(municipality => this.municipalityCache.set(municipality.id, municipality))
    )
  }

  getProvinces$(provinceId: number) {
    const cachedValue = this.provinceCache.get(provinceId)
    if (cachedValue) return of(cachedValue)

    return this.italianRegistryProxyService.getProvinciaById$(provinceId).pipe(
      throwIfLobbyErrorCode,
      map(dto => provinceDtoToProvince(dto.data)),
      tap(province => this.provinceCache.set(province!.id, province!))
    )
  }

  getBusinessSector$(businessSectorId: number) {
    return this.businessSectorProxyService.getBusinessSectorById$(businessSectorId).pipe(
      throwIfLobbyErrorCode,
      map(dto => businessSectorDtoToBusinessSector(dto.data)!)
    )
  }

  createLead$(lead: Lead): Observable<void> {
    return this.leadProxyService.postLead$(this.mapLeadToCreateDto(lead), false).pipe(map(() => void 0))
  }

  createLeadDryRun$(lead: Lead): Observable<LeadDryrunError[]> {
    return this.leadProxyService.postLead$(this.mapLeadToCreateDto(lead), true).pipe(dryrunErrors)
  }

  private mapLeadToCreateDto(lead: Lead): LeadCreateDto {
    const piva = lead.fiscalIds.find(f => f.kind == FiscalIdKind.PartitaIva)
    const italianFiscalCode = lead.fiscalIds.find(f => f.kind == FiscalIdKind.CfPersonaFisica)

    return {
      emails: lead.contacts.filter(c => c.type == ContactType.Email).map(e => { return { description: e.description, value: e.value } }),
      phoneNumbers: lead.contacts.filter(c => c.type == ContactType.Phone).map(e => { return { description: e.description, value: e.value } }),
      rawValues: lead.rawValues.map(rv => { return { name: rv.description, value: rv.value } }),
      partiteIva: piva ? [{ description: '', isPreferred: true, partitaIva: piva!.value }] : [],
      cfPersoneFisiche: italianFiscalCode ? [{ description: '', isPreferred: true, codiceFiscale: italianFiscalCode!.value }] : [],
      cfPersoneGiuridiche: [],
      provinciaId: lead.province?.id ?? null,
      comuneId: lead.municipality?.id ?? null,
      businessSectorId: lead.businessSector?.id ?? null,
      description: lead.description,
      comment: lead.comment,
      businessName: lead.businessName
    }
  }

}
